import React, { useState, useRef, useEffect } from "react";
import {
  User,
  Heart,
  ShoppingBag,
  Package,
  Minus,
  Plus,
  LayoutDashboard,
  Pencil,
  Bell,
  Pin,
  Gift,
  LogOut,
  X,
  Trash2,
} from "lucide-react";
import "./header.css";
import "./dropdown.css";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import axios from "axios";
import { server } from "../../server";
import toast from "react-hot-toast";
import { removeFromWishlist } from "../../redux/reducers/wishlistSlice";
import {
  removeFromCartAsync,
  updateQuantityAsync,
} from "../../redux/reducers/cart";
import { trackBeginCheckout } from "../../analytics/analytics";
import { cldUrl } from "../../utils/cloudinary";
import api, { setAuthToken } from "../../axiosInstance";

const ACCOUNT_LINKS = [
  { label: "Dashboard", to: "/dashboard", icon: LayoutDashboard },
  { label: "My orders", to: "/orders", icon: Package },
  { label: "My Info", to: "/account/info", icon: Pencil },
  { label: "Notifications", to: "/notifications", icon: Bell },
  { label: "Addresses", to: "/addresses", icon: Pin },
  { label: "Gift Cards", to: "/refer", icon: Gift },
];

function Logo({ size = 24 }) {
  return (
    <Link
      to="/"
      className="fn-logo"
      style={{ fontSize: size, color: "#111", textDecoration: "none" }}
    >
      <span className="light" style={{ color: "#111" }}>
        NINETY
      </span>
      <span className="bold" style={{ color: "#111" }}>
        ONE
      </span>
    </Link>
  );
}

function BagIcon({ size = 24 }) {
  const { cart } = useSelector((state) => state.cart);
  const bagCount = cart.reduce((sum, item) => sum + (item.quantity || 0), 0);

  return (
    <span className="fn-bag-wrap">
      <ShoppingBag size={size} strokeWidth={1.5} />
      {bagCount > 0 && <span className="fn-bag-badge">{bagCount}</span>}
    </span>
  );
}

function IconButton({ children, label, onClick, ...rest }) {
  return (
    <button
      type="button"
      aria-label={label}
      className="fn-icon-btn"
      onClick={onClick}
      {...rest}
    >
      {children}
    </button>
  );
}

/* ── helpers ── */
function getProductImage(item) {
  if (item.image) return item.image;
  if (item.images?.[0]?.url) return item.images[0].url;
  if (item.variants?.[0]?.images?.[0]?.url)
    return item.variants[0].images[0].url;
  if (item.variants?.[0]?.image) return item.variants[0].image;
  return null;
}

/* ============================================================
   WISHLIST DROPDOWN
   ============================================================ */
function WishlistItemRow({ item, onRemove }) {
  const image = getProductImage(item);
  const hasDiscount =
    item.originalPrice && Number(item.originalPrice) !== Number(item.basePrice);
  const navigate = useNavigate();

  return (
    <div className="fn-cart-item">
      <div
        className="fn-cart-item-image"
        style={{
          backgroundImage: image ? `url(${cldUrl(image, 160)})` : undefined,
          backgroundColor: image ? undefined : "#f5f5f5",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      />
      <div className="fn-cart-item-body">
        <p className="fn-cart-item-title">{item.name}</p>
        <p className="fn-cart-item-price">
          Kes{item.basePrice}
          {hasDiscount && (
            <span className="fn-cart-item-strike">Kes{item.originalPrice}</span>
          )}
        </p>
        <div className="fn-cart-item-footer">
          <button
            type="button"
            className="fn-cart-viewbag"
            onClick={() => navigate(`/product/${item.slug}`)}
            style={{
              fontSize: 10,
              padding: "2px 8px",
              minHeight: 22,
              lineHeight: 1,
            }}
          >
            View Product
          </button>

          <button
            type="button"
            className="fn-cart-save"
            onClick={() => onRemove?.(item._id)}
            style={{
              fontSize: 10,
              padding: "2px 8px",
              minHeight: 22,
              lineHeight: 1,
              display: "inline-flex",
              alignItems: "center",
              gap: 4,
              color: "#d32f2f",
            }}
          >
            <Trash2 size={10} strokeWidth={2} />
            Remove
          </button>
        </div>
      </div>
    </div>
  );
}

function WishlistPreview({ isVisible, items, onClose, onRemove }) {
  const navigate = useNavigate();

  return (
    <div className={`fn-cart-preview ${isVisible ? "is-open" : ""}`}>
      <div className="fn-cart-card">
        <div
          className="fn-cart-top"
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <p className="fn-cart-subtotal" style={{ margin: 0 }}>
            Wishlist <strong>({items.length})</strong>
          </p>
          <button
            type="button"
            className="fn-icon-btn"
            onClick={onClose}
            aria-label="Close wishlist"
          >
            <X size={18} />
          </button>
        </div>

        <div className="fn-cart-items">
          {items.length === 0 ? (
            <p
              className="fn-cart-item-title"
              style={{ textAlign: "center", padding: "24px 0", color: "#666" }}
            >
              Your wishlist is empty.
            </p>
          ) : (
            items.map((item) => (
              <WishlistItemRow key={item._id} item={item} onRemove={onRemove} />
            ))
          )}
        </div>

        {items.length > 0 && (
          <div className="fn-cart-cta-row" style={{ marginTop: 12 }}>
            <button
              type="button"
              className="fn-cart-checkout"
              onClick={() => {
                onClose();
                navigate("/wishlist");
              }}
            >
              View Full Wishlist
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

/* ============================================================
   LOGIN PROMPT (guests)
   ============================================================ */
function LoginPrompt({ isVisible, onClose, onLogin }) {
  return (
    <div className={`fn-cart-preview ${isVisible ? "is-open" : ""}`}>
      <div className="fn-cart-card" style={{ minWidth: 280 }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: 12,
          }}
        >
          <p className="fn-cart-subtotal" style={{ margin: 0 }}>
            Wishlist
          </p>
          <button
            type="button"
            className="fn-icon-btn"
            onClick={onClose}
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>

        <div style={{ textAlign: "center", padding: "16px 8px" }}>
          <Heart
            size={40}
            strokeWidth={1.2}
            style={{ marginBottom: 12, color: "#ccc" }}
          />
          <p
            className="fn-cart-item-title"
            style={{ marginBottom: 16, color: "#444" }}
          >
            Sign in to save your favorites
          </p>
          <button type="button" className="fn-cart-checkout" onClick={onLogin}>
            Sign In
          </button>
        </div>
      </div>
    </div>
  );
}

/* ============================================================
   ACCOUNT DROPDOWN
   ============================================================ */
function AccountPreview({ isVisible, user, onSignOut }) {
  const firstName = user?.firstName || user?.name || "there";

  return (
    <div className={`fn-account-preview ${isVisible ? "is-open" : ""}`}>
      <div className="fn-account-card">
        <p className="fn-account-greeting">Hi, {firstName}</p>
        <ul className="fn-account-menu">
          {ACCOUNT_LINKS.map(({ label, to, icon: Icon }) => (
            <li key={label}>
              <Link to={to} className="fn-account-menu-item">
                <Icon size={17} strokeWidth={1.6} />
                <span>{label}</span>
              </Link>
            </li>
          ))}

          {user?.role === "admin" && (
            <li>
              <Link to="/admin-dashboard" className="fn-account-menu-item">
                <LayoutDashboard size={17} strokeWidth={1.6} />
                <span>Admin Dashboard</span>
              </Link>
            </li>
          )}
        </ul>

        <button
          type="button"
          className="fn-account-signout"
          onClick={onSignOut}
        >
          <LogOut size={17} strokeWidth={1.6} />
          <span>Sign Out</span>
        </button>
      </div>
    </div>
  );
}

/* ============================================================
   CART DROPDOWN
   ============================================================ */
function CartItemRow({ item }) {
  const dispatch = useDispatch();

  const handleQtyChange = (delta) => {
    const newQty = item.quantity + delta;
    if (newQty < 1) return;
    if (newQty > item.stock) return;
    dispatch(
      updateQuantityAsync(item.productId, item.color?._id, item.size, newQty),
    );
  };

  const handleRemove = () => {
    dispatch(removeFromCartAsync(item.productId, item.color?._id, item.size));
  };

  return (
    <div className="fn-cart-item">
      <div className="fn-cart-item-image">
        {item.image ? (
          <img
            src={cldUrl(item.image, 160)}
            alt={item.name}
            loading="lazy"
            width={80}
            height={100}
          />
        ) : (
          <div
            className={`fn-cart-item-placeholder fn-cart-tone-${item.tone}`}
          />
        )}
        {item.badge && <span className="fn-cart-item-badge">{item.badge}</span>}
      </div>

      <div className="fn-cart-item-body">
        <div className="fn-cart-item-header">
          <p className="fn-cart-item-title">{item.name}</p>
          <button
            type="button"
            className="fn-cart-item-remove"
            onClick={handleRemove}
            aria-label={`Remove ${item.name} from bag`}
          >
            <X size={14} strokeWidth={2} />
          </button>
        </div>

        <p
          className={`fn-cart-item-price ${
            item.originalPrice ? "fn-cart-item-price--sale" : ""
          }`}
        >
          {item.currency?.toLowerCase?.() === "kes"
            ? "Kes"
            : item.currency || "Kes"}{" "}
          {item.basePrice?.toLocaleString?.()}
          {item.originalPrice && (
            <span className="fn-cart-item-strike">
              {" "}
              {item.currency?.toLowerCase?.() === "kes"
                ? "Kes"
                : item.currency || "Kes"}{" "}
              {item.originalPrice?.toLocaleString?.()}
            </span>
          )}
        </p>

        {item.promo && <p className="fn-cart-item-promo">{item.promo}</p>}

        <p className="fn-cart-item-meta">
          Size: {item.size} &nbsp;|&nbsp; Color: {item.color?.name}
          <span
            className="fn-cart-item-swatch"
            style={{ backgroundColor: item.color?.hex || item.color }}
          />
        </p>

        {item.stock <= 5 && item.stock > 0 && (
          <span className="fn-cart-item-urgency">
            ⚡ Only {item.stock} left
          </span>
        )}

        <div className="fn-cart-item-footer">
          <div className="fn-cart-qty">
            <button
              type="button"
              aria-label={`Decrease quantity for ${item.name}`}
              onClick={() => handleQtyChange(-1)}
              disabled={item.quantity <= 1}
            >
              <Minus size={11} strokeWidth={2} />
            </button>
            <span>{item.quantity}</span>
            <button
              type="button"
              aria-label={`Increase quantity for ${item.name}`}
              onClick={() => handleQtyChange(1)}
              disabled={item.quantity >= item.stock}
            >
              <Plus size={11} strokeWidth={2} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function CartPreview({ isVisible }) {
  const navigate = useNavigate();
  const { cart } = useSelector((state) => state.cart);

  const handleCheckout = () => {
    if (!cart || cart.length === 0) return;
    trackBeginCheckout(cart);
    navigate("/checkout");
  };

  const bagCount = cart.reduce((sum, item) => sum + (item.quantity || 0), 0);

  const subtotal = cart.reduce(
    (sum, item) => sum + (item.basePrice || 0) * (item.quantity || 0),
    0,
  );

  const freeShippingThreshold = 10000;
  const qualifiesForFreeShipping = subtotal >= freeShippingThreshold;

  return (
    <div className={`fn-cart-preview ${isVisible ? "is-open" : ""}`}>
      <div className="fn-cart-card">
        <div className="fn-cart-top">
          <p className="fn-cart-subtotal">
            Subtotal: <strong>Kes {subtotal.toLocaleString()}</strong>
          </p>
          <div className="fn-cart-cta-row">
            <Link to="/cart" className="fn-cart-viewbag">
              View bag ({bagCount})
            </Link>

            {cart.length > 0 && (
              <button
                type="button"
                className="fn-cart-checkout"
                onClick={handleCheckout}
              >
                Proceed to Checkout
              </button>
            )}
          </div>
        </div>

        <div
          className={`fn-cart-shipping ${
            qualifiesForFreeShipping ? "fn-cart-shipping--free" : ""
          }`}
        >
          <Package size={16} strokeWidth={1.8} />
          {qualifiesForFreeShipping
            ? "You earned Free Standard Shipping!"
            : `Kes ${(
                freeShippingThreshold - subtotal
              ).toLocaleString()} away from free shipping`}
        </div>

        <div className="fn-cart-items">
          {cart.length === 0 ? (
            <p className="fn-cart-empty">Your bag is empty.</p>
          ) : (
            cart.map((item) => (
              <CartItemRow
                key={`${item.productId}-${item.color?._id}-${item.size}`}
                item={item}
              />
            ))
          )}
        </div>
      </div>
    </div>
  );
}

/* ============================================================
   MAIN HEADER
   ============================================================ */
export default function Header() {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [cartOpen, setCartOpen] = useState(false);
  const [accountOpen, setAccountOpen] = useState(false);
  const [wishlistOpen, setWishlistOpen] = useState(false);

  const { isAuthenticated, loading, user } = useSelector((state) => state.user);
  const { wishlist = [] } = useSelector((state) => state.wishlist);

  const cartRefs = useRef([]);
  const accountRefs = useRef([]);
  const wishlistRefs = useRef([]);

  useEffect(() => {
    function handleClickOutside(event) {
      const insideCart = cartRefs.current.some(
        (el) => el && el.contains(event.target),
      );
      if (!insideCart) setCartOpen(false);

      const insideAccount = accountRefs.current.some(
        (el) => el && el.contains(event.target),
      );
      if (!insideAccount) setAccountOpen(false);

      const insideWishlist = wishlistRefs.current.some(
        (el) => el && el.contains(event.target),
      );
      if (!insideWishlist) setWishlistOpen(false);
    }

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("touchstart", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
    };
  }, []);

  const toggleCart = () => setCartOpen((prev) => !prev);

  const handleWishlistClick = () => {
    if (loading) return;
    setWishlistOpen((prev) => !prev);
  };

  const handleAccountMouseEnter = () => {
    if (loading || !isAuthenticated) return;
    setAccountOpen(true);
  };

  const handleAccountMouseLeave = () => setAccountOpen(false);

  const handleAccountClick = () => {
    if (loading) return;
    if (!isAuthenticated) {
      navigate("/login");
      return;
    }
    setAccountOpen((prev) => !prev);
  };

  const handleSignOut = async () => {
    try {
      const res = await api.get(`/user/logout`);
      toast.success(res.data.message);
      setAccountOpen(false);
      setWishlistOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.message || "Logout failed");
    } finally {
      setAuthToken(null); // clear the Bearer fallback
      localStorage.removeItem("token"); // clear any legacy token

      setTimeout(() => {
        navigate("/", { replace: true });
        window.location.reload();
      }, 1000);
    }
  };

  /* ── Render helpers ── */
  const renderWishlistSection = (iconSize = 24, idx = 0) => {
    const wishlistCount = wishlist.length;

    return (
      <div
        className="fn-cart-wrap"
        ref={(el) => {
          wishlistRefs.current[idx] = el;
        }}
      >
        <IconButton label="Wishlist" onClick={handleWishlistClick}>
          <div className="fn-icon-badge-wrap">
            <Heart
              size={iconSize}
              strokeWidth={1.5}
              className="fn-heart-icon"
              fill={wishlistCount > 0 ? "currentColor" : "none"}
            />
            {wishlistCount > 0 && (
              <span className="fn-count-badge">
                {wishlistCount > 99 ? "99+" : wishlistCount}
              </span>
            )}
          </div>
        </IconButton>

        {isAuthenticated ? (
          <WishlistPreview
            isVisible={wishlistOpen}
            items={wishlist}
            onClose={() => setWishlistOpen(false)}
            onRemove={(id) => dispatch(removeFromWishlist(id))}
          />
        ) : (
          <LoginPrompt
            isVisible={wishlistOpen}
            onClose={() => setWishlistOpen(false)}
            onLogin={() => navigate("/login")}
          />
        )}
      </div>
    );
  };

  const renderAccountSection = (iconSize = 24, idx = 0) => (
    <div
      className="fn-account-wrap"
      ref={(el) => {
        accountRefs.current[idx] = el;
      }}
      onMouseEnter={handleAccountMouseEnter}
      onMouseLeave={handleAccountMouseLeave}
    >
      <IconButton
        label="Account"
        onClick={handleAccountClick}
        aria-haspopup="menu"
        aria-expanded={accountOpen}
      >
        <User size={iconSize} strokeWidth={1.5} />
      </IconButton>
      {isAuthenticated && (
        <AccountPreview
          isVisible={accountOpen}
          user={user}
          onSignOut={handleSignOut}
        />
      )}
    </div>
  );

  const renderCartSection = (idx = 0) => (
    <div
      className="fn-cart-wrap"
      ref={(el) => {
        cartRefs.current[idx] = el;
      }}
    >
      <IconButton label="Bag" onClick={toggleCart}>
        <BagIcon />
      </IconButton>
      <CartPreview isVisible={cartOpen} />
    </div>
  );

  return (
    <header className="fn-header" style={{ maxWidth: "88rem" }}>
      {/* ---------- DESKTOP (>=1024px) ---------- */}
      <div className="fn-desktop">
        <div className="fn-d-row1">
          <Logo size={24} />

          <div className="fn-d-icons">
            {renderAccountSection(24, 0)}
            {renderWishlistSection(24, 0)}
            {renderCartSection(0)}
          </div>
        </div>
      </div>

      {/* ---------- TABLET (768px - 1023px) ---------- */}
      <div className="fn-tablet">
        <div className="fn-t-row1">
          <Logo size={20} />
          <div className="fn-t-icons">
            {renderAccountSection(20, 1)}
            {renderWishlistSection(20, 1)}
            {renderCartSection(1)}
          </div>
        </div>
      </div>

      {/* ---------- PHONE (<768px) ---------- */}
      <div className="fn-phone">
        <div className="fn-p-row1">
          <Logo size={18} />
          <div className="fn-p-icons">
            {renderWishlistSection(19, 2)}
            {renderCartSection(2)}
          </div>
        </div>
      </div>
    </header>
  );
}
